import { useState, useCallback, useEffect, useRef } from 'react'

import { createAssistantMessage, createUserMessage, MESSAGE_STATUS, isUserMessage } from 'components/agent_hub_components'

/** Stable empty default — avoids useEffect loops when callers pass fresh `[]` literals. */
export const EMPTY_MESSAGES = []

const shouldSyncInitialMessages = (next, previous) => {
  if (next === previous) return false
  if (next.length === 0 && previous.length === 0) return false
  return true
}

/**
 * Custom hook for managing chat messages state
 * Provides functionality to add user messages, assistant messages, and check loading state
 */
export const useMessages = (initialMessages = EMPTY_MESSAGES) => {
  const [messages, setMessages] = useState(initialMessages)
  const lastInitialMessagesRef = useRef(initialMessages)

  // Update messages when initialMessages change (for thread loading)
  useEffect(() => {
    if (!shouldSyncInitialMessages(initialMessages, lastInitialMessagesRef.current)) {
      lastInitialMessagesRef.current = initialMessages
      return
    }
    setMessages(initialMessages)
    lastInitialMessagesRef.current = initialMessages
  }, [initialMessages])

  const addUserMessage = useCallback((content, status = MESSAGE_STATUS.SUCCESS, attachments = null) => {
    const userMessage = {
      ...createUserMessage({ content, status }),
      ...(attachments?.length ? { attachments } : {})
    }
    setMessages((prev) => [...prev, userMessage])
    return userMessage
  }, [])

  const addAssistantMessage = useCallback((content, status = MESSAGE_STATUS.SUCCESS, queryId, responseId = null) => {
    const assistantMessage = createAssistantMessage({ content, status, queryId, responseId })
    setMessages((prev) => [...prev, assistantMessage])
    return assistantMessage
  }, [])

  const clearMessages = useCallback(() => {
    setMessages([])
  }, [])

  // Check if the most recent message is from user (indicates loading state)
  const shouldShowLoading = useCallback(() => {
    const mostRecentMessage = messages[messages.length - 1]
    return isUserMessage(mostRecentMessage)
  }, [messages])

  return {
    messages,
    addUserMessage,
    addAssistantMessage,
    clearMessages,
    shouldShowLoading
  }
}
